if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      static MOBILE_QUERY = '(max-width: 749px)';

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.addButton = this.querySelector('.sticky-atc__add');
        this.addButtonLabel = this.addButton?.querySelector('span');
        this.barSizeSelect = this.querySelector('.sticky-atc__size-select:not(.sticky-atc__size-select--sheet)');
        this.barSizeField = this.querySelector('.sticky-atc__field--size.sticky-atc__field--desktop');
        this.priceTarget = this.querySelector('.sticky-atc__price');

        this.sheet = this.querySelector('.sticky-atc__sheet');
        this.sheetOverlay = this.querySelector('.sticky-atc__sheet-overlay');
        this.sheetClose = this.querySelector('.sticky-atc__sheet-close');
        this.sheetSizeSelect = this.querySelector('.sticky-atc__size-select--sheet');
        this.sheetSizeField = this.querySelector('.sticky-atc__sheet-field--size');
        this.sheetPriceTarget = this.querySelector('.sticky-atc__sheet-price');
        this.sheetAddButton = this.querySelector('.sticky-atc__sheet-add');
        this.sheetAddButtonLabel = this.sheetAddButton?.querySelector('span');

        this.awaitingVariantUpdate = false;
        this.pendingSubmit = false;
        this.variantUpdateTimeout = null;

        this.onBarAdd = this.onBarAdd.bind(this);
        this.onSheetAdd = this.onSheetAdd.bind(this);
        this.onSizeChange = this.onSizeChange.bind(this);
        this.onSheetKeydown = this.onSheetKeydown.bind(this);
        this.closeSheet = this.closeSheet.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);

        this.bindEvents();
        this.setupObserver();
        this.subscribeToUpdates();
        this.syncFromMain();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.clearVariantUpdateTimeout();
        document.removeEventListener('keydown', this.onSheetKeydown);
        this.unlockScroll();
      }

      get isMobile() {
        return window.matchMedia(StickyAddToCart.MOBILE_QUERY).matches;
      }

      get mainSubmit() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get mainPrice() {
        return document.getElementById(`price-${this.sectionId}`);
      }

      get sizeGroup() {
        const selects = document.getElementById(`variant-selects-${this.sectionId}`);
        return selects?.querySelector('[data-size-option]') || null;
      }

      get scrollAnchor() {
        return this.mainSubmit?.closest('.product-form__buttons') || this.mainSubmit;
      }

      get productFormElement() {
        return document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
      }

      bindEvents() {
        this.addButton?.addEventListener('click', this.onBarAdd);
        this.sheetAddButton?.addEventListener('click', this.onSheetAdd);
        this.barSizeSelect?.addEventListener('change', (event) => this.onSizeChange(event.target));
        this.sheetSizeSelect?.addEventListener('change', (event) => this.onSizeChange(event.target));
        this.sheetOverlay?.addEventListener('click', this.closeSheet);
        this.sheetClose?.addEventListener('click', this.closeSheet);
        document.addEventListener('keydown', this.onSheetKeydown);
      }

      subscribeToUpdates() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;

        this.syncFromMain();
        this.awaitingVariantUpdate = false;
        this.clearVariantUpdateTimeout();
        this.restoreScrollPosition();

        const wasPendingSubmit = this.pendingSubmit;
        this.flushPendingSubmit();

        if (wasPendingSubmit && !this.pendingSubmit && this.sheet?.hasAttribute('open')) {
          this.closeSheet();
        }
      }

      restoreScrollPosition() {
        if (this.preserveScrollY == null) return;

        const scrollY = this.preserveScrollY;
        this.preserveScrollY = null;

        const restore = () => window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
        restore();
        requestAnimationFrame(restore);
        window.setTimeout(restore, 0);
      }

      clearVariantUpdateTimeout() {
        if (this.variantUpdateTimeout === null) return;
        window.clearTimeout(this.variantUpdateTimeout);
        this.variantUpdateTimeout = null;
      }

      scheduleVariantUpdateTimeout() {
        this.clearVariantUpdateTimeout();
        this.variantUpdateTimeout = window.setTimeout(() => {
          this.variantUpdateTimeout = null;
          this.awaitingVariantUpdate = false;
          this.syncFromMain();
          this.flushPendingSubmit();
          this.pendingSubmit = false;
        }, 5000);
      }

      setupObserver() {
        const anchor = this.scrollAnchor;
        if (!anchor) return;

        this.observer = new IntersectionObserver(
          (entries) => this.toggleVisible(!entries[0].isIntersecting),
          { threshold: 0 }
        );
        this.observer.observe(anchor);
      }

      toggleVisible(visible) {
        this.classList.toggle('sticky-atc--visible', visible);
        if (!visible) this.closeSheet();
      }

      onSheetKeydown(event) {
        if (event.key === 'Escape' && this.sheet?.hasAttribute('open')) {
          event.preventDefault();
          this.closeSheet();
        }
      }

      openSheet() {
        if (!this.sheet || !this.isMobile) return;
        this.syncFromMain();
        this.sheet.hidden = false;
        requestAnimationFrame(() => {
          this.sheet.setAttribute('open', '');
          this.lockScroll();
          this.sheetClose?.focus();
        });
      }

      closeSheet() {
        if (!this.sheet?.hasAttribute('open')) return;
        this.sheet.removeAttribute('open');
        this.unlockScroll();
        window.setTimeout(() => {
          if (!this.sheet.hasAttribute('open')) this.sheet.hidden = true;
        }, 320);
      }

      lockScroll() {
        document.body.classList.add('overflow-hidden');
      }

      unlockScroll() {
        if (!this.sheet?.hasAttribute('open')) {
          document.body.classList.remove('overflow-hidden');
        }
      }

      onBarAdd() {
        const main = this.mainSubmit;
        if (!main) return;

        if (this.isMobile) {
          if (main.hasAttribute('disabled') && !this.awaitingVariantUpdate) return;
          this.openSheet();
          return;
        }

        if (main.hasAttribute('disabled') && !this.awaitingVariantUpdate) return;
        this.submitToCart();
      }

      onSheetAdd() {
        const main = this.mainSubmit;
        if (!main) return;
        if (main.hasAttribute('disabled') && !this.awaitingVariantUpdate) return;

        this.submitToCart();

        if (!this.pendingSubmit) this.closeSheet();
      }

      submitToCart() {
        if (this.awaitingVariantUpdate) {
          this.pendingSubmit = true;
          this.scheduleVariantUpdateTimeout();
          return;
        }

        this.performSubmit();
      }

      performSubmit() {
        const productFormEl = this.productFormElement;
        const form = productFormEl?.querySelector('form');
        const variantIdInput = form?.querySelector('[name="id"]');
        const main = this.mainSubmit;

        if (!form || !variantIdInput?.value || main?.hasAttribute('disabled')) return;

        const activeButton = this.getActiveAddButton();
        if (!activeButton || activeButton.disabled || activeButton.getAttribute('aria-disabled') === 'true') return;

        this.setButtonLoading(activeButton, true);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };

        if (config.headers) {
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
        }

        const formData = new FormData(form);
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(activeButton);
        }
        config.body = formData;

        const cartAddUrl = (typeof routes !== 'undefined' && routes.cart_add_url) || window.routes?.cart_add_url || '/cart/add';

        fetch(`${cartAddUrl}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-add-to-cart',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              productFormEl?.handleErrorMessage?.(response.description);
              return;
            }

            if (!cart) {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-add-to-cart',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            if (typeof CartPerformance !== 'undefined') {
              CartPerformance.measure('add:paint-updated-sections', () => {
                cart.renderContents(response);
              });
            } else {
              cart.renderContents(response);
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.setButtonLoading(activeButton, false);
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      getActiveAddButton() {
        if (this.isMobile && this.sheet?.hasAttribute('open')) {
          return this.sheetAddButton;
        }

        return this.addButton;
      }

      setButtonLoading(button, isLoading) {
        if (!button) return;

        const spinner = button.querySelector('.loading__spinner');
        if (isLoading) {
          button.classList.add('loading');
          button.setAttribute('aria-disabled', 'true');
          spinner?.classList.remove('hidden');
          return;
        }

        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }

      dispatchStickySizeChange(target) {
        target.dispatchEvent(
          new CustomEvent('change', {
            bubbles: true,
            detail: { stickyAtc: true },
          })
        );
      }

      flushPendingSubmit() {
        if (!this.pendingSubmit) return;

        const stickySize = this.getActiveStickySize();
        const mainSize = this.getMainSizeValue();

        if (!stickySize && !mainSize) {
          this.pendingSubmit = false;
          this.performSubmit();
          return;
        }

        if (stickySize === mainSize) {
          this.pendingSubmit = false;
          this.performSubmit();
        }
      }

      getActiveStickySize() {
        if (this.isMobile && this.sheet?.hasAttribute('open') && this.sheetSizeSelect) {
          return this.sheetSizeSelect.value;
        }

        return this.barSizeSelect?.value || null;
      }

      getMainSizeValue() {
        const group = this.sizeGroup;
        if (!group) return null;

        const mainSelect = group.querySelector('select');
        if (mainSelect) return mainSelect.value;

        const checked = group.querySelector('input[type="radio"]:checked');
        return checked?.value || null;
      }

      onSizeChange(sourceSelect) {
        const value = sourceSelect.value;
        const group = this.sizeGroup;
        if (!group) return;

        let didChange = false;
        const mainSelect = group.querySelector('select');

        if (mainSelect) {
          if (mainSelect.value !== value) {
            mainSelect.value = value;
            this.preserveScrollY = window.scrollY;
            this.dispatchStickySizeChange(mainSelect);
            didChange = true;
          }
        } else {
          const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
          const target = inputs.find((input) => input.value === value);
          if (target && !target.checked) {
            target.checked = true;
            this.preserveScrollY = window.scrollY;
            this.dispatchStickySizeChange(target);
            didChange = true;
          }
        }

        this.syncSizeSelectValue(value, sourceSelect);

        if (didChange) {
          this.awaitingVariantUpdate = true;
          this.clearVariantUpdateTimeout();
        }
      }

      syncSizeSelectValue(value, sourceSelect = null) {
        [this.barSizeSelect, this.sheetSizeSelect].forEach((select) => {
          if (!select || select === sourceSelect) return;
          if (Array.from(select.options).some((option) => option.value === value && !option.disabled)) {
            select.value = value;
          }
        });
      }

      syncSizeFromMain() {
        const group = this.sizeGroup;
        const selects = [this.barSizeSelect, this.sheetSizeSelect].filter(Boolean);

        if (!group || selects.length === 0) {
          if (this.barSizeField) this.barSizeField.hidden = true;
          if (this.sheetSizeField) this.sheetSizeField.hidden = true;
          return;
        }

        if (this.barSizeField) this.barSizeField.hidden = false;
        if (this.sheetSizeField) this.sheetSizeField.hidden = false;

        const mainSelect = group.querySelector('select');
        let optionsData = [];
        let selectedValue = null;

        if (mainSelect) {
          optionsData = Array.from(mainSelect.options).map((option) => ({
            value: option.value,
            label: option.dataset.stickyLabel || option.textContent.trim(),
            disabled: option.disabled,
            selected: option.selected,
          }));
          selectedValue = mainSelect.value;
        } else {
          const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
          optionsData = inputs.map((input) => ({
            value: input.value,
            label: this.cleanLabel(group.querySelector(`label[for="${input.id}"]`)) || input.value,
            disabled: input.classList.contains('disabled'),
            selected: input.checked,
          }));
          const checked = inputs.find((input) => input.checked);
          selectedValue = checked ? checked.value : null;
        }

        selects.forEach((select) => {
          select.innerHTML = '';
          optionsData.forEach((data) => {
            const option = document.createElement('option');
            option.value = data.value;
            option.textContent = data.label;
            option.disabled = data.disabled;
            option.selected = data.selected;
            select.appendChild(option);
          });
          if (selectedValue !== null) select.value = selectedValue;
        });
      }

      cleanLabel(label) {
        if (!label) return '';
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.label-unavailable, .visually-hidden').forEach((node) => node.remove());
        return clone.textContent.replace(/\s+/g, ' ').trim();
      }

      syncButtonState() {
        const main = this.mainSubmit;
        const buttons = [
          { button: this.addButton, label: this.addButtonLabel },
          { button: this.sheetAddButton, label: this.sheetAddButtonLabel },
        ];

        buttons.forEach(({ button, label }) => {
          if (!button || !main) return;
          button.disabled = main.hasAttribute('disabled');
          const text = main.querySelector('span')?.textContent?.trim();
          if (text && label) label.textContent = text;
        });
      }

      syncFromMain() {
        if (this.mainPrice) {
          if (this.priceTarget) this.priceTarget.innerHTML = this.mainPrice.innerHTML;
          if (this.sheetPriceTarget) this.sheetPriceTarget.innerHTML = this.mainPrice.innerHTML;
        }

        this.syncButtonState();
        this.syncSizeFromMain();
      }
    }
  );
}
